import React, { useState, useEffect } from 'react';
import { 
  X, Save, Calendar, FileText, Tag, Euro, 
  Sparkles, FileEdit, CheckCircle2, Loader2 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from '../hooks/use-toast';
import { apiService } from '../services/api';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const EditDocumentModal = ({ isOpen, onClose, document, onSave }) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    merchant_name: '',
    amount: '',
    date: '',
    due_date: '',
    category: 'general',
    summary: '',
    status: 'pending'
  });

  // Belge değiştiğinde formu güncelle
  useEffect(() => {
    if (document) {
      setFormData({
        merchant_name: document.merchant_name || document.titleKey || '',
        amount: document.amount || '',
        date: document.date ? new Date(document.date).toISOString().split('T')[0] : '',
        due_date: document.due_date ? new Date(document.due_date).toISOString().split('T')[0] : '',
        category: document.category || 'general',
        summary: document.descriptionKey || document.summary || '',
        status: document.status || 'pending'
      });
    }
  }, [document]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value, name) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // YAPAY ZEKA İLE OTOMATİK DOLDURMA (Simülasyon)
  const handleAutoFill = async () => {
      setAiLoading(true);
      setTimeout(() => {
          setFormData(prev => ({
              ...prev,
              merchant_name: prev.merchant_name || "AI Tarafından Bulunan Satıcı",
              amount: prev.amount || "123.45",
              date: prev.date || new Date().toISOString().split('T')[0],
              category: prev.category === 'general' ? 'office' : prev.category,
              summary: "Yapay zeka tarafından belgenin içeriği analiz edildi ve önemli bilgiler çıkarıldı."
          }));
          setAiLoading(false);
          toast({
              title: t('ai_success_title') || "AI Analizi Tamamlandı",
              description: t('ai_success_desc') || "Belge bilgileri otomatik olarak dolduruldu.",
              className: "bg-emerald-500 text-white border-0"
          });
      }, 1500);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const updatedDoc = await apiService.documents.update(document.id, {
        ...formData,
        amount: parseFloat(formData.amount) || 0,
      });

      toast({ 
        title: t('success_saved') || "Başarıyla Kaydedildi", 
        description: t('doc_updated_desc') || "Belge bilgileri güncellendi.",
        className: "bg-blue-600 text-white border-0" 
      });
      
      if (onSave) onSave(updatedDoc);
      onClose();
    } catch (error) {
      console.error("Update Error:", error);
      toast({ 
        title: t('error_generic') || "Hata", 
        description: t('update_failed') || "Güncelleme başarısız oldu.", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !document) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          
          {/* Backdrop (Arka Plan Karartma) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />

          {/* Modal Container - Flexbox ile Merkezlendi */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
            className="relative w-full max-w-3xl border border-white/10 bg-[#0B1120]/90 shadow-2xl backdrop-blur-xl rounded-[2.5rem] overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* HEADER */}
            <div className="flex items-center justify-between p-6 md:p-8 border-b border-white/10 bg-white/[0.02] shrink-0">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-gradient-to-br from-orange-600 to-orange-500 rounded-2xl text-white shadow-lg shadow-orange-600/20">
                        <FileEdit size={24} />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-white uppercase tracking-tight leading-none">
                            EDİTÖR <span className="text-orange-500">TERMİNALİ</span>
                        </h2>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                            İSTİHBARAT MODÜLÜ V4.0
                        </p>
                    </div>
                </div>
                <button 
                    onClick={onClose} 
                    className="p-3 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/10 text-slate-400 hover:text-white transition-colors"
                >
                    <X size={20} />
                </button>
            </div>

            {/* BODY - Scrollable Area */}
            <div className="p-6 md:p-8 space-y-8 overflow-y-auto custom-scrollbar flex-1">
                
                {/* AI AUTO-FILL BUTTON */}
                <Button 
                    onClick={handleAutoFill}
                    disabled={aiLoading}
                    className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white font-black uppercase tracking-widest h-14 rounded-2xl shadow-lg shadow-orange-600/20 transition-all active:scale-[0.98] flex items-center justify-center gap-3 text-xs"
                >
                    {aiLoading ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
                    {t('ai_autofill_btn') || 'YAPAY ZEKA OTOMATİK DOLDURMAYI BAŞLAT'}
                </Button>

                <div className="space-y-6">
                    {/* MERCHANT & AMOUNT */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">
                                {t('label_merchant') || 'SATICI / BAŞLIK'}
                            </Label>
                            <div className="relative">
                                <FileText className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                <Input 
                                    name="merchant_name"
                                    value={formData.merchant_name}
                                    onChange={handleInputChange}
                                    className="pl-12 h-14 bg-black/20 border-white/10 rounded-2xl text-white font-bold placeholder:text-slate-700 focus:bg-blue-500/5 focus:border-blue-500/50 transition-all"
                                    placeholder="Örn: Apple Store"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">
                                {t('label_amount') || 'TUTAR'} (€)
                            </Label>
                            <div className="relative">
                                <Euro className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                <Input 
                                    type="number"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleInputChange}
                                    className="pl-12 h-14 bg-black/20 border-white/10 rounded-2xl text-white font-mono font-bold placeholder:text-slate-700 focus:bg-blue-500/5 focus:border-blue-500/50 transition-all"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                    </div>

                    {/* CATEGORY & STATUS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">
                                {t('label_category') || 'KATEGORİ'}
                            </Label>
                            <Select value={formData.category} onValueChange={(value) => handleSelectChange(value, 'category')}>
                                <SelectTrigger className="h-14 bg-black/20 border-white/10 rounded-2xl text-white font-bold pl-4 focus:ring-0 focus:border-blue-500/50 transition-all">
                                    <div className="flex items-center gap-3">
                                        <Tag size={18} className="text-slate-500" />
                                        <SelectValue placeholder="Kategori Seçin" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent className="bg-[#111827] border-white/10 text-white rounded-xl z-[60]">
                                    <SelectItem value="general">{t('category_general') || 'Genel'}</SelectItem>
                                    <SelectItem value="tax">{t('category_tax') || 'Vergi'}</SelectItem>
                                    <SelectItem value="office">{t('category_office') || 'Ofis / İş'}</SelectItem>
                                    <SelectItem value="rent">{t('category_housing') || 'Kira / Konut'}</SelectItem>
                                    <SelectItem value="car">{t('category_car') || 'Araç / Seyahat'}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                             <Label className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">
                                DURUM
                            </Label>
                             <Select value={formData.status} onValueChange={(value) => handleSelectChange(value, 'status')}>
                                <SelectTrigger className="h-14 bg-black/20 border-white/10 rounded-2xl text-white font-bold pl-4 focus:ring-0 focus:border-blue-500/50 transition-all">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle2 size={18} className={formData.status === 'verified' ? "text-emerald-500" : "text-slate-500"} />
                                        <SelectValue placeholder="Durum Seçin" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent className="bg-[#111827] border-white/10 text-white rounded-xl z-[60]">
                                    <SelectItem value="pending">Beklemede / İşleniyor</SelectItem>
                                    <SelectItem value="verified" className="text-emerald-400">Doğrulandı / Güvenli</SelectItem>
                                    <SelectItem value="warning" className="text-orange-400">İnceleme Gerekiyor</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* DATES */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">
                                {t('label_date') || 'TARİH'}
                            </Label>
                            <div className="relative">
                                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                <Input 
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    className="pl-12 h-14 bg-black/20 border-white/10 rounded-2xl text-white font-mono focus:bg-blue-500/5 focus:border-blue-500/50 transition-all"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1 flex items-center gap-2">
                                SON ÖDEME TARİHİ
                                <span className="text-[8px] bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">Opsiyonel</span>
                            </Label>
                            <div className="relative">
                                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                <Input 
                                    type="date"
                                    name="due_date"
                                    value={formData.due_date}
                                    onChange={handleInputChange}
                                    className="pl-12 h-14 bg-black/20 border-white/10 rounded-2xl text-white font-mono focus:bg-blue-500/5 focus:border-blue-500/50 transition-all placeholder:text-slate-600"
                                />
                            </div>
                        </div>
                    </div>

                    {/* SUMMARY / NOTES */}
                    <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">
                            {t('ai_summary_title') || 'DETAYLI NOTLAR / AI ÖZETİ'}
                        </Label>
                        <Textarea 
                            name="summary"
                            value={formData.summary}
                            onChange={handleInputChange}
                            className="min-h-[100px] bg-black/20 border-white/10 rounded-2xl text-white text-sm leading-relaxed p-4 focus:bg-blue-500/5 focus:border-blue-500/50 transition-all resize-none"
                            placeholder="Belge hakkında notlar veya yapay zeka özeti..."
                        />
                    </div>

                </div>
            </div>

            {/* FOOTER ACTIONS */}
            <div className="p-6 md:p-8 border-t border-white/10 bg-white/[0.02] flex items-center justify-end gap-4 shrink-0">
                <button 
                    onClick={onClose}
                    className="px-6 text-slate-400 hover:text-white font-bold uppercase tracking-widest text-xs transition-colors"
                >
                    {t('action_cancel') || 'İPTAL'}
                </button>
                <Button 
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 md:flex-none md:w-64 bg-white text-[#0B1120] hover:bg-blue-50 font-black uppercase tracking-widest text-xs h-14 rounded-xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                    {t('action_save') || 'İSTİHBARATI KAYDET'}
                </Button>
            </div>

          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EditDocumentModal;